import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FolderDto } from '@picflow/types';
import { BehaviorSubject, Observable, map, of, switchMap, take, tap } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class FolderService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private state = inject(StateService);
  private getAllService = inject(GetAllService);

  onFolderUpdated$ = new BehaviorSubject<FolderDto>(undefined);
  onFolderDeleted$ = new BehaviorSubject<FolderDto>(undefined);
  onFolderCreated$ = new BehaviorSubject<FolderDto>(undefined);

  private updateUpdater = this.onFolderUpdated$.subscribe(folder => {
    this.loader.update<FolderDto[]>('folders', folders => {
      const index = folders.findIndex(f => f.id === folder.id);
      if (index === -1) return folders;
      folders[index] = folder;
      return folders;
    });
  });

  private deleteUpdater = this.onFolderDeleted$.subscribe(folder => {
    this.loader.update<FolderDto[]>('folders', folders => folders.filter(f => f.id !== folder.id));
  });

  private createUpdater = this.onFolderCreated$.subscribe(folder => {
    this.loader.update<FolderDto[]>('folders', folders => {
      folders.push(folder);
      return folders;
    });
  });

  // Note: it is possible to pass a parent folder id but we don't because we also need to fetch subfolders and subsubfolders
  // Which is less efficient then just doing one requst for all folders
  public getFolders(parentFolderId?: string): Observable<FolderDto[]> {
    if (!this.state.activeTenant) return of([]);
    return this.loader
      .get(
        `folders`,
        () => this.getAllService.getAll<FolderDto>(`${this.config.apiEndpoint}/folders`),
        1000 * 60 * 10,
        false
      )
      .pipe(
        map(folders => {
          if (!parentFolderId) return folders;
          if (parentFolderId === 'none') return folders.filter(f => !f.parent_folder);
          return folders.filter(f => f.parent_folder === parentFolderId);
        })
      );
  }

  public getAllFolderDecendants(folderId: string): Observable<FolderDto[]> {
    return this.getFolders().pipe(
      take(1),
      map(folders => this.recursiveFilterFolders(folderId, folders))
    );
  }

  private recursiveFilterFolders(folderId: string, folders: FolderDto[]): FolderDto[] {
    const allFolders = [];
    const validFolders = folders.filter(folder => folder.parent_folder === folderId);
    validFolders.forEach(f => {
      allFolders.push(f);
      allFolders.push(...this.recursiveFilterFolders(f.id, folders));
    });
    return allFolders;
  }

  public getFolder(id: string): Observable<FolderDto> {
    return this.loader.getFromCache<FolderDto[]>('folders').pipe(
      switchMap(folders => {
        if (folders?.find(g => g.id === id)) return of(folders.find(g => g.id === id));
        return this.loader.get(`folders_${id}`, () =>
          this.http.get<FolderDto>(this.config.apiEndpoint + '/folders/' + id)
        );
      })
    );
  }

  public createFolder(title: string, parentFolderId?: string) {
    const params = { title, parent_folder: parentFolderId };
    return this.http.post<FolderDto>(`${this.config.apiEndpoint}/folders`, params).pipe(
      tap(folder => {
        this.onFolderCreated$.next(folder);
      })
    );
  }

  private findTopFolder(folder: FolderDto) {
    if (!folder.parent_folder) return of(folder);
    return this.getFolders().pipe(
      take(1),
      map(folders => {
        if (folder.id === folder.parent_folder) throw new Error('Folder cannot be its own parent');
        return folders.find(f => f.id === folder.parent_folder);
      })
    );
  }

  public updateFolder(folder: FolderDto) {
    this.findTopFolder(folder).subscribe(); // making sure we don't have a circular reference
    if (folder.id === folder.parent_folder) {
      throw new Error('Folder cannot be its own parent');
    }
    return this.http.patch<FolderDto>(`${this.config.apiEndpoint}/folders/${folder.id}`, folder).pipe(
      tap(() => {
        this.onFolderUpdated$.next(folder);
      })
    );
  }

  public deleteFolder(folder: FolderDto) {
    return this.http.delete(`${this.config.apiEndpoint}/folders/${folder.id}`).pipe(
      tap(() => {
        this.onFolderDeleted$.next(folder);
      })
    );
  }
}
